import { memo, useMemo, useCallback } from "react";

import { useProduct } from "@shopify/hydrogen-react";
import {
  type ProductOptionValue,
  type MediaImage,
} from "@shopify/hydrogen-react/storefront-api-types";

import { Slide } from "@components/Carousel";
import { ColorSwatch } from "@components/ColorSwatch";
import { handleize, isColorOption } from "@react/utilities";
import { SelectedVariantAvailability } from "@root/tablescape/constants";
import { formatProduct, formatProductVariant } from "@tablescape/utilities";
import { tw } from "classnames";

type Props = {
  optionName: string;
  optionValue: ProductOptionValue;
};

const styles = {
  slide: tw("flex", "items-center"),
};

const ProductCardOptionBase = ({ optionName, optionValue }: Props) => {
  const { product, selectedOptions, setSelectedOption, variants } =
    useProduct();

  const formattedProduct = useMemo(() => formatProduct(product), [product]);

  const colorOption = useMemo(
    () => formattedProduct.options?.find((option) => isColorOption(option)),
    [formattedProduct.options]
  );

  const getVariantFromOptionValue = useCallback(
    (value: string) => {
      if (!colorOption) {
        return;
      }

      const foundVariant = variants.find((variant) =>
        variant.selectedOptions.some(
          (option) => option.name === colorOption.name && option.value === value
        )
      );

      if (foundVariant) {
        return formatProductVariant(foundVariant);
      }
    },
    [variants, colorOption]
  );

  const variant = useMemo(
    () => getVariantFromOptionValue(optionValue.name),
    [getVariantFromOptionValue, optionValue.name]
  );

  const handleSwatchMouseEnter = useCallback(
    (name: string, value: string) => () => {
      setSelectedOption(name, value);
    },
    [setSelectedOption]
  );

  const isVariantAvailable = useMemo(
    () =>
      variant?.availableForSale &&
      (variant?.shippingNote
        ? handleize(variant.shippingNote) !==
          SelectedVariantAvailability.SOLD_OUT
        : true),
    [variant?.availableForSale, variant?.shippingNote]
  );

  const isVariantBackordered = useMemo(
    () =>
      !variant?.disableWaitlist &&
      (variant?.shippingNote
        ? handleize(variant.shippingNote) !==
          SelectedVariantAvailability.SOLD_OUT
        : false),
    [variant?.disableWaitlist, variant?.shippingNote]
  );

  const isVariantActive = useMemo(
    () => selectedOptions[optionName] === optionValue.name,
    [optionName, optionValue.name, selectedOptions]
  );

  if (
    !variant ||
    variant.hidden ||
    !(optionValue.swatch?.color || optionValue.swatch?.image)
  ) {
    return null;
  }

  return (
    <Slide
      className={styles.slide}
      onMouseEnter={handleSwatchMouseEnter(optionName, optionValue.name)}
    >
      <ColorSwatch
        active={isVariantActive}
        alt={variant.title}
        available={isVariantAvailable}
        backordered={isVariantBackordered}
        checked={isVariantActive}
        hex={optionValue.swatch.color}
        image={(optionValue.swatch.image as MediaImage)?.image?.url}
        optionLabel={variant.optionLabel}
        size={{ sm: "tiny", md: "mini" }}
      />
    </Slide>
  );
};

export const ProductCardOption = memo(ProductCardOptionBase);
